import type OlMap from "ol/Map";
import { unByKey } from "ol/Observable";
import type { FitOptions } from "ol/View";
import type { Options as OlZoomControlOptions } from "ol/control/Zoom";
import { easeOut } from "ol/easing";
import type { EventsKey } from "ol/events";
import type { Extent } from "ol/extent";
import { get as getProjection, transformExtent } from "ol/proj";
import type { ProjectionLike } from "ol/proj";
import { useCallback, useEffect, useState } from "react";

import { MapControl, useNgwMapContext } from "@nextgis/react-ngw-map";
import type { ControlOptions } from "@nextgis/webmap";

import { useTranslation } from "../../i18n";

interface ShowZoomLevelOptions {
  displayDecimals?: boolean;
}

export interface ZoomControlOptions extends Pick<
  OlZoomControlOptions,
  "delta" | "duration" | "className" | "zoomInTipLabel" | "zoomOutTipLabel"
> {
  extent?: Extent;
  extentProjection?: ProjectionLike;
  fitOptions?: FitOptions;
  homeTipLabel?: string;
  showZoomLevel?: boolean | ShowZoomLevelOptions;
}

export type ZoomControlProps = ControlOptions & ZoomControlOptions;

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 11l8-7 8 7" />
      <path d="M6 10v9h12v-9" />
      <path d="M10 19v-5h4v5" />
    </svg>
  );
}

function getTargetExtent(
  olMap: OlMap,
  extent: Extent,
  extentProjection: ProjectionLike,
) {
  const viewProjection = olMap.getView().getProjection();
  const sourceProjection = getProjection(extentProjection);

  if (!sourceProjection) {
    return extent;
  }

  return transformExtent(extent, sourceProjection, viewProjection);
}

export function ZoomControl({
  className = "ol-zoom map-zoom-control",
  delta = 1,
  duration = 250,
  extent,
  extentProjection = "EPSG:4326",
  fitOptions,
  homeTipLabel,
  order,
  position = "top-left",

  zoomInTipLabel,
  zoomOutTipLabel,
}: ZoomControlProps) {
  const { t } = useTranslation();
  const { ngwMap } = useNgwMapContext();
  const olMap = ngwMap.mapAdapter?.map as OlMap | undefined;

  const [canZoomIn, setCanZoomIn] = useState(true);
  const [canZoomOut, setCanZoomOut] = useState(true);

  const updateAvailability = useCallback(() => {
    if (!olMap) return;

    const view = olMap.getView();
    const current = view.getZoom();
    if (typeof current !== "number") {
      setCanZoomIn(false);
      setCanZoomOut(false);
      return;
    }

    const absDelta = Math.abs(delta);
    const nextInc = view.getConstrainedZoom(current + absDelta);
    const nextDec = view.getConstrainedZoom(current - absDelta);

    setCanZoomIn(typeof nextInc === "number" && nextInc > current);
    setCanZoomOut(typeof nextDec === "number" && nextDec < current);
  }, [delta, olMap]);

  useEffect(() => {
    if (!olMap) return;

    const view = olMap.getView();
    const keys: EventsKey[] = [
      view.on("change:resolution", updateAvailability),
      olMap.on("moveend", updateAvailability),
    ].flat();

    updateAvailability();

    return () => {
      unByKey(keys);
    };
  }, [olMap, updateAvailability]);

  const zoomBy = useCallback(
    (value: number) => {
      if (!olMap) return;

      const view = olMap.getView();
      const current = view.getZoom();
      if (typeof current !== "number") return;

      const newZoom = view.getConstrainedZoom(current + value);
      if (typeof newZoom !== "number") return;

      if (duration > 0) {
        if (view.getAnimating()) {
          view.cancelAnimations();
        }
        view.animate({
          duration,
          easing: easeOut,
          zoom: newZoom,
        });
      } else {
        view.setZoom(newZoom);
      }
    },
    [duration, olMap],
  );

  const zoomIn = useCallback(() => zoomBy(delta), [delta, zoomBy]);
  const zoomOut = useCallback(() => zoomBy(-delta), [delta, zoomBy]);

  const goHome = useCallback(() => {
    if (!extent || !olMap) return;

    olMap.getView().fit(getTargetExtent(olMap, extent, extentProjection), {
      ...fitOptions,
    });
  }, [extent, extentProjection, fitOptions, olMap]);

  if (!olMap) return null;

  return (
    <MapControl
      bar
      className={className}
      position={position}
      order={order}
      margin
    >
      <button
        type="button"
        disabled={!canZoomIn}
        className="ol-zoom-in"
        title={zoomInTipLabel ?? t("zoom.in")}
        onClick={zoomIn}
      >
        +
      </button>

      {/* {showZoomLevel && (
        <ZoomLevel
          olMap={olMap}
          {...(typeof showZoomLevel === "object" ? showZoomLevel : {})}
        />
      )} */}

      <button
        type="button"
        disabled={!canZoomOut}
        className="ol-zoom-out"
        title={zoomOutTipLabel ?? t("zoom.out")}
        onClick={zoomOut}
      >
        -
      </button>

      {extent && (
        <button
          type="button"
          className="home-button"
          title={homeTipLabel ?? t("zoom.home")}
          onClick={goHome}
        >
          <HomeIcon className="tool-svg" />
        </button>
      )}
    </MapControl>
  );
}
